import { useState, useEffect } from 'react';
import { useAuth } from '../services/authContext';
import { Pane, Button, Dialog, Switch, Text, Heading } from 'evergreen-ui';
import consentService from '../services/consentService';

const COOKIE_NAME = 'cookie_consent';
const COOKIE_MAX_AGE = 31536000;

const readCookie = (name) => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    if (match) return decodeURIComponent(match[2]);
    return null;
};

const setCookie = (name, value, maxAge) => {
    document.cookie = `${name}=${encodeURIComponent(value)};path=/;max-age=${maxAge};SameSite=Lax`;
};

const CookieConsent = ({ userId = null }) => {
    const [consent, setConsent] = useState(null);
    const [showDialog, setShowDialog] = useState(false);
    const [localChoices, setLocalChoices] = useState({ necessary: true, analytics: false, marketing: false, preferences: false });
    const { user, getUserId } = useAuth();

    useEffect(() => {
        const existing = readCookie(COOKIE_NAME);
        if (existing) {
            try {
                const parsed = JSON.parse(existing);
                setConsent(parsed);
            } catch (e) {
                console.error('Errore parsing cookie consent:', e);
            }
        }

        const openFromOutside = () => {
            const existing = readCookie(COOKIE_NAME);
            let parsed = null;
            if (existing) {
                try {
                    parsed = JSON.parse(existing);
                } catch (e) {
                    console.error('Errore parsing cookie quando apro preferenze:', e);
                }
            }
            setLocalChoices(parsed || { necessary: true, analytics: false, marketing: false, preferences: false });
            setShowDialog(true);
        };
        
        window.openCookiePreferences = openFromOutside;
        window.addEventListener('openCookiePreferences', openFromOutside);

        return () => {
            try { delete window.openCookiePreferences; } catch (e) { window.openCookiePreferences = undefined; }
            window.removeEventListener('openCookiePreferences', openFromOutside);
        };
    }, []);

    const save = async (choices) => {
        try {
            setCookie(COOKIE_NAME, JSON.stringify(choices), COOKIE_MAX_AGE);
            setConsent(choices);
            const effectiveUserId = userId ?? (typeof getUserId === 'function' ? getUserId() : null) ?? (user ? user.id : null);
            await consentService.saveConsent(choices, null, effectiveUserId);
        } catch (err) {
            console.error('Errore salvataggio consenso:', err);
        }
    };

    const acceptAll = async () => {
        const choices = { necessary: true, analytics: true, marketing: true, preferences: true };
        await save(choices);
    };

    const rejectAll = async () => {
        const choices = { necessary: true, analytics: false, marketing: false, preferences: false };
        await save(choices);
    };

    const openManage = () => {
        setLocalChoices(consent || { necessary: true, analytics: false, marketing: false, preferences: false });
        setShowDialog(true);
    };

    const confirmManage = async () => {
        await save(localChoices);
        setShowDialog(false);
    };

    return (
        <>
            {!consent && (
                <Pane position="fixed" bottom={16} right={16} left={16} zIndex={1000} display="flex" justifyContent="center">
                    <Pane background="tint1" padding={16} borderRadius={8} width="100%" maxWidth={960} boxShadow="0 4px 12px rgba(0,0,0,0.15)">
                        <Pane display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={16}>
                            <Pane flex={1} minWidth={280}>
                                <Heading size={500}>Usiamo cookie per migliorare il sito</Heading>
                                <Text display="block" marginTop={8}>Questo sito utilizza cookie propri e di terze parti per funzionamento, analisi e marketing. Puoi accettare tutto o gestire le preferenze.</Text>
                            </Pane>
                            <Pane display="flex" flexWrap="wrap" gap={8} width="100%" justifyContent="flex-end" flex={1} minWidth={280}>
                                <Button onClick={rejectAll} appearance="minimal">Rifiuta tutto</Button>
                                <Button onClick={openManage} appearance="primary" intent="none">Gestisci preferenze</Button>
                                <Button onClick={acceptAll} appearance="primary" intent="success">Accetta tutto</Button>
                            </Pane>
                        </Pane>
                    </Pane>
                </Pane>
            )}

            <Dialog isShown={showDialog} title="Preferenze cookie" intent="success" onCloseComplete={() => setShowDialog(false)} confirmLabel="Salva" onConfirm={confirmManage}>
                <Pane display="flex" flexDirection="column" gap={12}>
                    <Pane>
                        <Text fontWeight={600}>Necessari</Text>
                        <Text size={300} display="block">Cookie tecnici necessari al funzionamento del sito. Sempre attivi.</Text>
                    </Pane>

                    <Pane display="flex" alignItems="center" justifyContent="space-between">
                        <Pane>
                            <Text fontWeight={600}>Analitica</Text>
                            <Text size={300} display="block">Cookie per analisi anonime.</Text>
                        </Pane>
                        <Switch checked={localChoices.analytics} onChange={(e) => setLocalChoices((p) => ({ ...p, analytics: e.target.checked }))} />
                    </Pane>

                    <Pane display="flex" alignItems="center" justifyContent="space-between">
                        <Pane>
                            <Text fontWeight={600}>Marketing</Text>
                            <Text size={300} display="block">Cookie per advertising e terze parti.</Text>
                        </Pane>
                        <Switch checked={localChoices.marketing} onChange={(e) => setLocalChoices((p) => ({ ...p, marketing: e.target.checked }))} />
                    </Pane>

                    <Pane display="flex" alignItems="center" justifyContent="space-between">
                        <Pane>
                            <Text fontWeight={600}>Preferenze</Text>
                            <Text size={300} display="block">Impostazioni lingua e preferenze interfaccia.</Text>
                        </Pane>
                        <Switch checked={localChoices.preferences} onChange={(e) => setLocalChoices((p) => ({ ...p, preferences: e.target.checked }))} />
                    </Pane>
                </Pane>
            </Dialog>
        </>
    );
};

export default CookieConsent;
